import React, { useState, useEffect } from "react";
import { Card, Button, Table, Space, Tag, Modal, message } from "antd";
import {
  DeleteOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import * as ReviewsApi from "../api/Reviews";

const { confirm } = Modal;

interface LastReviewData {
  key: string;
  value: string;
  marketplace?: string;
  sellerId?: string;
  status?: string;
}

const LastReviewManager: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<LastReviewData[]>([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const keys = await ReviewsApi.getLastReviewKeys();
      const formattedData: LastReviewData[] = Object.entries(keys).map(
        ([key, value]) => {
          // Парсим ключ формата reviews:last_review:{marketplace}:{seller_id}:{status}
          const parts = key.split(":");
          const marketplace = parts[2] || "";
          const sellerId = parts[3] || "";
          const status = parts[4] || "";

          return {
            key,
            value,
            marketplace,
            sellerId,
            status,
          };
        }
      );
      setData(formattedData);
    } catch (error) {
      message.error("Ошибка при загрузке данных");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const clearAllKeys = () => {
    confirm({
      title: "Аварийный сброс всех last_review",
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>
            <strong>ВНИМАНИЕ!</strong> Это действие удалит все сохраненные
            позиции выгрузки отзывов.
          </p>
          <p>
            После сброса все выгрузки отзывов начнутся заново с самого начала.
          </p>
          <p>
            Количество ключей для удаления: <strong>{data.length}</strong>
          </p>
          <p>Вы уверены, что хотите продолжить?</p>
        </div>
      ),
      okText: "Да, очистить все",
      okType: "danger",
      cancelText: "Отмена",
      async onOk() {
        setLoading(true);
        try {
          const result = await ReviewsApi.clearAllLastReview();
          message.success(result.message);
          await loadData(); // Перезагружаем данные
        } catch (error) {
          message.error("Ошибка при очистке данных");
          console.error(error);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const clearSpecificKey = (key: string) => {
    confirm({
      title: "Удаление конкретного ключа",
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Вы хотите удалить ключ:</p>
          <p>
            <code>{key}</code>
          </p>
          <p>
            Это приведет к сбросу позиции выгрузки для данного
            маркетплейса/продавца.
          </p>
        </div>
      ),
      okText: "Да, удалить",
      okType: "danger",
      cancelText: "Отмена",
      async onOk() {
        setLoading(true);
        try {
          const result = await ReviewsApi.clearSpecificLastReview(key);
          if (result.success) {
            message.success(result.message);
            await loadData(); // Перезагружаем данные
          } else {
            message.error(result.message);
          }
        } catch (error) {
          message.error("Ошибка при удалении ключа");
          console.error(error);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const columns = [
    {
      title: "Маркетплейс",
      dataIndex: "marketplace",
      key: "marketplace",
      render: (text: string) => (
        <Tag
          color={
            text === "ozon"
              ? "blue"
              : text === "wildberries"
              ? "purple"
              : "default"
          }
        >
          {text.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "ID Продавца",
      dataIndex: "sellerId",
      key: "sellerId",
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      render: (text: string) => <Tag color="processing">{text}</Tag>,
    },
    {
      title: "Последняя позиция",
      dataIndex: "value",
      key: "value",
      render: (text: string) => {
        try {
          const parsed = JSON.parse(text);
          return (
            <div style={{ fontSize: "12px" }}>
              <div>
                <strong>UUID:</strong> {parsed.uuid?.substring(0, 8)}...
              </div>
              <div>
                <strong>Rating:</strong> {parsed.rating}
              </div>
              <div>
                <strong>Timestamp:</strong>{" "}
                {new Date(parsed.timestamp).toLocaleString()}
              </div>
            </div>
          );
        } catch {
          return (
            <code style={{ fontSize: "11px" }}>{text.substring(0, 50)}...</code>
          );
        }
      },
    },
    {
      title: "Действия",
      key: "actions",
      render: (_: any, record: LastReviewData) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => clearSpecificKey(record.key)}
          size="small"
        >
          Удалить
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Аварийное управление позициями выгрузки отзывов (Last Review)"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadData}
              loading={loading}
            >
              Обновить
            </Button>
            <Button
              type="primary"
              danger
              icon={<ClearOutlined />}
              onClick={clearAllKeys}
              disabled={data.length === 0}
              loading={loading}
            >
              Очистить все ({data.length})
            </Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <p>
            <strong>Last Review</strong> — это механизм сохранения позиции при
            выгрузке отзывов из маркетплейсов. Он позволяет продолжить выгрузку
            с места остановки, а не начинать заново.
          </p>
          <p>
            <strong>Аварийная очистка</strong> может потребоваться, если
            выгрузка "зависла" на определенной позиции или нужно принудительно
            начать выгрузку заново.
          </p>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="key"
          loading={loading}
          size="small"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Всего: ${total} ключей`,
          }}
          locale={{
            emptyText: "Нет сохраненных позиций last_review",
          }}
        />
      </Card>
    </div>
  );
};

export default LastReviewManager;
